import React, { useState, useEffect, useRef } from 'react';
import proptypes from 'prop-types';
import {
  params,
  extend,
  string as stringType,
  useLazyDataModel,
  customType,
  arrayOf
} from '@thd-nucleus/data-sources';
import {
  Alert, Button, Link, LoadingButton, DrawerBody, DrawerFooter, Typography
} from '@one-thd/sui-atomic-components';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { useConfigService } from '@thd-nucleus/experience-context';
import { triggerAnalytics } from '../helpers';
import {
  PROJECT_ALREADY_EXISTS_ERROR_1,
  PROJECT_ALREADY_EXISTS_ERROR_2,
  PROJECT_ALREADY_EXISTS_MESSAGE,
  PROJECT_CREATE_CLICK,
  PROJECT_CREATE_SUCCESS,
  PROJECT_CREATE_OVERLAY,
  PROJECT_CREATE_ACCOUNT_ERROR
} from '../constants';

import { ProjectInformationForm } from '../ProjectForms/ProjectInformationForm';
import { JobsiteInformationForm } from '../ProjectForms/JobsiteInformationForm';

const { func, bool, string } = proptypes;

// Convert 12-hour AM/PM display values to 24-hour HH:MM format for the API
const convertTo24HourFormat = (timeStr) => {
  if (!timeStr) {
    return timeStr;
  }
  const match = timeStr.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/i);
  if (!match) {
    return timeStr;
  }
  let hour = parseInt(match[1], 10);
  const minutes = match[2];
  const period = match[3].toUpperCase();
  if (period === 'AM' && hour === 12) {
    hour = 0;
  }
  if (period === 'PM' && hour !== 12) {
    hour += 12;
  }
  return `${String(hour).padStart(2, '0')}:${minutes}`;
};

// Convert strings containing "yes"/"no" to boolean for hasStairs
const normalizeHasStairs = (hasStairs) => {
  if (!hasStairs || typeof hasStairs !== 'string') {
    return hasStairs;
  }
  const hasStairsLower = hasStairs.toLowerCase();
  if (hasStairsLower.includes('yes')) {
    return true;
  }
  if (hasStairsLower.includes('no')) {
    return false;
  }
  return hasStairs;
};

// Remove empty/null values so unset fields are omitted from the request
const stripEmptyValues = (obj) => {
  const result = { ...obj };
  Object.keys(result).forEach((key) => {
    const val = result[key];
    if (val === '' || val === null) {
      delete result[key];
    }
  });
  return result;
};

const buildProcessedDeliveryPreferences = (deliveryPreferences) => {
  const processedDeliveryPreferences = { ...deliveryPreferences };

  if (processedDeliveryPreferences.jobSiteHoursStart) {
    processedDeliveryPreferences.jobSiteHoursStart = convertTo24HourFormat(
      processedDeliveryPreferences.jobSiteHoursStart
    );
  }
  if (processedDeliveryPreferences.jobSiteHoursEnd) {
    processedDeliveryPreferences.jobSiteHoursEnd = convertTo24HourFormat(
      processedDeliveryPreferences.jobSiteHoursEnd
    );
  }

  if (processedDeliveryPreferences.hasStairs) {
    processedDeliveryPreferences.hasStairs = normalizeHasStairs(processedDeliveryPreferences.hasStairs);
  }

  // Merge propertyTypeOther into propertyType so the API receives the actual value
  if (processedDeliveryPreferences.propertyType === 'Other'
    && processedDeliveryPreferences.propertyTypeOther) {
    processedDeliveryPreferences.propertyType = processedDeliveryPreferences.propertyTypeOther;
  }
  delete processedDeliveryPreferences.propertyTypeOther;

  return stripEmptyValues(processedDeliveryPreferences);
};

export const buildJobSiteAddress = (jobsiteInformation, isJobsiteInformationEnabled = false) => {
  // Switched off, the address is checked exactly as it was before DSCVR-5168. [DSCVR-5191]
  const jobAddress = isJobsiteInformationEnabled
    ? stripEmptyValues({ ...jobsiteInformation })
    : { ...jobsiteInformation };
  // Only send jobSiteAddress when every backend-required subfield is present; a partially
  // filled address (e.g. zip entered before the async city/state lookup resolves) otherwise
  // sends null for a NonNull String! and fails the mutation. That includes addressLine1:
  // pro-projects rejects a jobsite with no street (8009), so a zip entered without one goes
  // out as assortmentAddress instead (see buildAssortmentAddress). [DSCVR-5168]
  const requiredAddressFields = ['addressLine1', 'zipCode', 'city', 'state'];
  const hasAnyAddress = Object.keys(jobAddress).length !== 0;
  const hasAllRequiredAddress = requiredAddressFields.every(
    (field) => jobAddress[field] != null && jobAddress[field] !== ''
  );
  const isValidAddress = hasAnyAddress && hasAllRequiredAddress;
  if (isValidAddress) {
    jobAddress.country = 'US';
    jobAddress.addressType = 'JOB_SITE';
  }
  return { jobAddress, isValidAddress };
};

// A jobsite zip code on its own is a complete entry - it needs no Address Line 1 (DSCVR-4956).
// It can't travel inside jobSiteAddress, which needs a street, so createProject takes it as
// assortmentAddress: the zip, plus the city/state pair the lookup resolved once it has. A zip
// maps to several city/state pairs, so that pair is real input; it goes as a pair or not at
// all. A full address carries its own zip, so that path is left untouched. [DSCVR-5168]
export const buildAssortmentAddress = (jobsiteInformation, isValidAddress) => {
  const { zipCode, city, state } = jobsiteInformation || {};
  // Only a finished 5-digit zip is worth sending. The field accepts anything up to 5 characters
  // and the city/state lookup that would flag a bad one never fires below 5, so a half-typed zip
  // reaches here without an error on it.
  if (isValidAddress || !/^\d{5}$/.test(zipCode || '')) {
    return undefined;
  }
  return city && state ? { zipCode, city, state } : { zipCode };
};

// createProject as it was before DSCVR-5168. This is what the component's static dataModel - and
// so every QueryProvider that merges it, including add-to's - declares. [DSCVR-5191]
const createProjectParams = ({ withAssortmentAddress = false } = {}) => params({
  accountId: stringType().isRequired(),
  projectName: stringType().isRequired(),
  projectType: stringType(),
  estimatedStartDate: stringType(),
  estimatedEndDate: stringType(),
  // notes: stringType(),
  jobSiteAddress: customType('JobSiteAddress').shape({
    addressType: customType('addressType').enum(['ASSORTMENT_SIP', 'FULFILLMENT', 'JOB_SITE']),
    addressLine1: stringType().isRequired(),
    addressLine2: stringType(),
    zipCode: stringType().isRequired(),
    city: stringType().isRequired(),
    state: stringType().isRequired(),
    country: stringType().isRequired()
  }),
  assortmentZip: stringType(),
  // The AssortmentAddress input only exists on gateways with the DSCVR-5004 orchestration change,
  // and declaring an unknown type fails the whole mutation, so it is declared only for the
  // switched-on drawer. [DSCVR-5168, DSCVR-5191]
  ...(withAssortmentAddress ? {
    assortmentAddress: customType('AssortmentAddress').shape({
      zipCode: stringType().isRequired(),
      city: stringType(),
      state: stringType(),
      country: stringType()
    })
  } : {}),
  deliveryPreferences: customType('DeliveryPreferencesInput').shape({
    siteConditions: stringType(),
    deliveryInstructions: stringType(),
    preferredPackagePlacement: stringType(),
    securityCode: stringType(),
    lockboxCode: stringType(),
    lockboxLocation: stringType(),
    deliveryType: stringType(),
    propertyType: stringType(),
    hasStairs: customType('boolean')
  }),
  contacts: arrayOf(customType('ContactUpdate').shape({
    id: stringType(),
    name: stringType(),
    phone: stringType(),
    contactType: stringType()
  }))
}).mutation().shape({
  id: stringType(),
  projectId: stringType(),
  projectName: stringType()
});

// Handed to useLazyDataModel only while fs:isJobsiteInformationEnabled is on. Exported so specs can
// build switched-on mocks from the same operation the drawer sends.
export const createProjectWithAssortmentAddressDataModel = extend({
  createProject: createProjectParams({ withAssortmentAddress: true })
});

export const CreateAProjectContent = ({
  handleClose,
  onCreate,
  isAddToDrawer,
  open,
  message,
  showSuccessScreen,
  isCartExp
}) => {
  // DSCVR-4956 kill switch. Off restores the pre-ticket create-project drawer: Jobsite
  // Information back inside the "Project Details (Optional)" accordion, a zip on its own
  // treated as an incomplete address, and no assortmentAddress on the mutation.
  const isJobsiteInformationEnabled = !!useConfigService('fs:isJobsiteInformationEnabled');
  const [showErrors, setShowErrors] = useState(false);
  const [createdProject, setCreatedProject] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isProjectInfoError, setIsProjectInfoError] = useState(false);
  const [isJobsiteError, setIsJobsiteError] = useState(false);
  // AC1 made the top-level Delivery Zip Code required, so it needs its own channel into the
  // submit guard. It stays false on the switched-off path, where the field is not rendered.
  const [isDeliveryZipError, setIsDeliveryZipError] = useState(false);
  const [projectInformation, setProjectInformation] = useState({});
  const [deliveryPreferences, setDeliveryPreferences] = useState({});
  const [contacts, setContacts] = useState({});
  const [jobsiteInformation, setJobsiteInformation] = useState({});
  const [duplicateNameError, setDuplicateNameError] = useState('');
  const [generalError, setGeneralError] = useState('');
  const { svocID } = useThdCustomer();
  const projectForm = useRef();
  const isSubmittingRef = useRef(false);
  const [createProject, createProjectResponse] = useLazyDataModel('createProject', {
    variables: {},
    context: { withAuth: true },
    fetchPolicy: 'no-cache',
    // nucleus declares every dataModel param on the operation whether or not a value is sent, so
    // assortmentAddress may only be declared while the switch is on. Off, the operation is the
    // pre-DSCVR-5168 one, which every gateway accepts. [DSCVR-5191]
    ...(isJobsiteInformationEnabled ? { dataModel: createProjectWithAssortmentAddressDataModel } : {})
  });

  useEffect(() => {
    // Trigger analytics event when the drawer is opened - this is also used in CreateAProject.jsx when opening from the
    // CreateAProject button, but that doesn't apply when this component is used within another drawer.
    // This is the workaround to ensure the event is still triggered in that scenario. Rework needed when we have a
    // more robust analytics solution.
    if (isAddToDrawer) {
      triggerAnalytics(PROJECT_CREATE_OVERLAY, {});
    }
  }, []);

  const onClose = (_isCreated = false) => {
    setProjectInformation({});
    setJobsiteInformation({});
    setDeliveryPreferences({});
    setContacts({});
    setShowErrors(false);
    setGeneralError('');
    setCreatedProject(null);
    if (!isAddToDrawer) {
      handleClose();
    }
  };

  const handleFormSubmit = async (submitEvent) => {
    submitEvent.preventDefault();

    triggerAnalytics(PROJECT_CREATE_CLICK, {});

    const hasStartWithoutEnd = deliveryPreferences.jobSiteHoursStart
      && !deliveryPreferences.jobSiteHoursEnd;

    if (isProjectInfoError || isJobsiteError || isDeliveryZipError
      || !projectInformation.projectName || hasStartWithoutEnd) {
      setShowErrors(true);
      return;
    }

    if (!svocID) {
      // No SVOC id on the customer session (e.g. not signed in as a Pro customer). accountId is a
      // NonNull String!, so firing would fail server-side. svocID comes from window.THDCustomer,
      // set at page load, so a full refresh is what can actually recover it.
      setGeneralError(PROJECT_CREATE_ACCOUNT_ERROR);
      return;
    }

    const { jobAddress, isValidAddress } = buildJobSiteAddress(jobsiteInformation, isJobsiteInformationEnabled);

    const processedDeliveryPreferences = buildProcessedDeliveryPreferences(deliveryPreferences);

    // Normalize projectInformation for the API
    const processedProjectInformation = stripEmptyValues(projectInformation);

    // Clean contacts — remove empty string values but keep contactType
    const contactsCleaned = stripEmptyValues({ ...contacts, contactType: 'DELIVERY' });

    // Build estimatedStartDate/estimatedEndDate from cleaned data (not raw destructured values)
    const cleanedStartDate = processedProjectInformation.estimatedStartDate;
    const cleanedEndDate = processedProjectInformation.estimatedEndDate;
    // Remove from processedProjectInformation since they're handled explicitly below
    delete processedProjectInformation.estimatedStartDate;
    delete processedProjectInformation.estimatedEndDate;

    const finalVariables = {
      accountId: svocID,
      ...processedProjectInformation,
      jobSiteAddress: !isValidAddress ? undefined : { ...jobAddress },
      assortmentAddress: isJobsiteInformationEnabled
        ? buildAssortmentAddress(jobsiteInformation, isValidAddress)
        : undefined,
      deliveryPreferences: Object.keys(processedDeliveryPreferences).length
        ? processedDeliveryPreferences : undefined,
      contacts: [contactsCleaned],
      ...(cleanedStartDate
        ? { estimatedStartDate: new Date(cleanedStartDate).toISOString() }
        : {}),
      ...(cleanedEndDate
        ? { estimatedEndDate: new Date(cleanedEndDate).toISOString() }
        : {})
    };

    // Synchronous re-entry guard: React state updates are async, so isLoading alone can't stop
    // a second click landing before the re-render. The ref closes that sub-render gap.
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;
    setIsLoading(true);
    try {
      await createProject({ variables: finalVariables });
    } finally {
      // Re-enable on every path that leaves the component mounted (network error, or a GraphQL
      // error such as a duplicate name that the user must correct and resubmit). On success the
      // response effect closes the drawer and this unmounts before the queued update commits.
      isSubmittingRef.current = false;
      setIsLoading(false);
    }
  };

  const checkForAndSetAlreadyExistError = (error) => {
    const gqlErrors = error?.graphQLErrors;
    let errorMessage = '';
    if (gqlErrors?.some((err) => err?.message.includes(PROJECT_ALREADY_EXISTS_ERROR_1) || err?.message.includes(PROJECT_ALREADY_EXISTS_ERROR_2))) {
      errorMessage = PROJECT_ALREADY_EXISTS_MESSAGE;
    }
    setDuplicateNameError(errorMessage);
  };

  useEffect(() => {
    if (createProjectResponse?.data?.createProject && createProjectResponse?.data?.createProject?.id) {
      const isType = projectInformation?.projectType ? 'y' : 'n';
      // const isDesc = projectInformation?.notes ? 'y' : 'n';
      const isAddress = jobsiteInformation?.addressLine1 ? 'y' : 'n';
      const isZip = jobsiteInformation?.zipCode ? 'y' : 'n';

      // In the format of: “projects: project created: type <y/n>: desc <y/n>: address <y/n>: zip <y/n>”
      // eslint-disable-next-line max-len
      const settingsString = `projects: project created: type ${isType}: address ${isAddress}: zip ${isZip}`;
      triggerAnalytics(PROJECT_CREATE_SUCCESS,
        {
          settings: settingsString,
          projectId: createProjectResponse?.data?.createProject?.id
        }
      );

      onCreate(createProjectResponse?.data?.createProject);
      if (showSuccessScreen) {
        setCreatedProject(createProjectResponse?.data?.createProject);
      } else {
        onClose(true);
      }
    } else if (createProjectResponse?.error) {
      checkForAndSetAlreadyExistError(createProjectResponse?.error);
    }
  }, [createProjectResponse?.data, createProjectResponse?.error]);

  if (createdProject) {
    return (
      <>
        <DrawerBody data-component="CreateAProject">
          <Alert status="success" size="large">
            <Typography>
              <Link href={`/projects/${createdProject.id}`} weight="bold" title={createdProject.projectName}>
                {createdProject.projectName}
              </Link>
              {isCartExp
                ? ' created successfully. Assign it in cart to view this order in your project.'
                : ' created'}
            </Typography>
          </Alert>
        </DrawerBody>
        <DrawerFooter position="center">
          <Button
            data-testid="create-project-success-ok-button"
            variant="primary"
            fullWidth
            onClick={handleClose}
            type="button"
          > Ok
          </Button>
        </DrawerFooter>
      </>
    );
  }

  return (
    <>
      <DrawerBody data-component="CreateAProject">
        {message && (
          <div className="sui-mb-4">
            <Alert status="info">
              {message}
            </Alert>
          </div>
        )}
        {generalError && (
          <Alert
            status="error"
            onClose={() => setGeneralError('')}
          >
            {generalError}
          </Alert>
        )}
        <form
          className="sui-grid sui-gap-6"
          autoComplete="off"
          noValidate
          onSubmit={handleFormSubmit}
          ref={projectForm}
        >
          <Typography variant="body-lg" weight="bold">
            Project Information
          </Typography>
          <ProjectInformationForm
            showErrors={showErrors}
            projectInformation={projectInformation}
            setProjectInformation={setProjectInformation}
            isProjectInfoError={isProjectInfoError}
            setIsProjectInfoError={setIsProjectInfoError}
            deliveryPreferences={deliveryPreferences}
            setDeliveryPreferences={setDeliveryPreferences}
            contacts={contacts}
            setContacts={setContacts}
            open={open}
            duplicateNameError={duplicateNameError}
            jobsiteInformation={jobsiteInformation}
            setJobsiteInformation={setJobsiteInformation}
            isJobsiteError={isJobsiteError}
            setIsJobsiteError={setIsJobsiteError}
            isJobsiteInformationEnabled={isJobsiteInformationEnabled}
            setIsDeliveryZipError={setIsDeliveryZipError}
          />
        </form>
      </DrawerBody>
      <DrawerFooter position="center">
        <div className="sui-flex sui-flex-col sui-gap-2">
          <LoadingButton
            data-testid="create-project-submit-button"
            variant="primary"
            fullWidth
            loading={isLoading}
            disabled={isLoading}
            onClick={handleFormSubmit}
            type="submit"
          > Create Project
          </LoadingButton>
          <Button
            data-testid="create-project-cancel-button"
            variant="secondary"
            fullWidth
            disabled={isLoading}
            onClick={handleClose}
            type="button"
          > Cancel
          </Button>
        </div>
      </DrawerFooter>
    </>
  );
};

CreateAProjectContent.propTypes = {
  // callback when user clicks the form button
  // return parameters: { id, projectAccessType, project }
  onCreate: func,
  isAddToDrawer: bool,
  handleClose: func,
  open: bool,
  // optional informational message shown as an Alert at the top of the drawer body
  message: string,
  showSuccessScreen: bool,
  // when true, show the in-cart success copy instead of the default "created" copy
  isCartExp: bool
};

CreateAProjectContent.defaultProps = {
  onCreate: () => { },
  isAddToDrawer: false,
  handleClose: () => {},
  open: false,
  message: '',
  showSuccessScreen: false,
  isCartExp: false
};

CreateAProjectContent.displayName = 'CreateAProjectContent';

CreateAProjectContent.dataModel = extend(
  {
    createProject: createProjectParams()
  },
  JobsiteInformationForm
);
